/**
 * Build a short coupon code from coupon name + offer price (e.g. "PVC CARD" + ₹75 → PVC75).
 */

const MAX_CODE_LENGTH = 20;

const normalizeNamePart = (couponName) => {
  const cleaned = String(couponName || '')
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned) return 'CPN';

  const words = cleaned.split(' ').filter(Boolean);
  if (words.length >= 2) {
    const acronym = words.map((w) => w[0]).join('').replace(/[^A-Z0-9]/g, '');
    if (acronym.length >= 2) return acronym.slice(0, 6);
  }

  const primary = words[0].replace(/[^A-Z0-9]/g, '');
  return (primary || 'CPN').slice(0, 6);
};

const resolvePriceForCode = ({
  offerType,
  offerPrice,
  actualPrice,
  dayWisePricing,
}) => {
  if (offerType === 'increasing' && dayWisePricing && typeof dayWisePricing === 'object') {
    const days = Object.keys(dayWisePricing)
      .map((d) => Number(d))
      .filter((d) => !Number.isNaN(d) && d > 0)
      .sort((a, b) => a - b);
    if (days.length) {
      const first = parseFloat(dayWisePricing[String(days[0])]);
      if (!Number.isNaN(first) && first > 0) return first;
    }
  }

  const offer = parseFloat(offerPrice);
  if (!Number.isNaN(offer) && offer > 0) return offer;

  const actual = parseFloat(actualPrice);
  if (!Number.isNaN(actual) && actual > 0) return actual;

  return null;
};

const formatPricePart = (price) => {
  if (price == null || Number.isNaN(price)) return '';
  const rounded = Math.round(price * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  return String(rounded).replace('.', '');
};

/**
 * @returns {string|null} null if not enough data to build a meaningful code
 */
export const buildCouponCodeBase = ({
  couponName,
  offerType = 'fixed',
  offerPrice,
  actualPrice,
  dayWisePricing = {},
}) => {
  const name = String(couponName || '').trim();
  const price = resolvePriceForCode({
    offerType,
    offerPrice,
    actualPrice,
    dayWisePricing,
  });

  if (!name || price == null) return null;

  const namePart = normalizeNamePart(name);
  const pricePart = formatPricePart(price);
  if (!pricePart) return null;

  return `${namePart}${pricePart}`.slice(0, MAX_CODE_LENGTH);
};

export const normalizeCouponCode = (code) =>
  String(code || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

/**
 * @param {string} baseCode
 * @param {Set<string>|string[]} takenCodes
 * @param {string} [excludeCode] — current coupon when editing (allowed to keep same code)
 */
export const ensureUniqueCouponCode = (baseCode, takenCodes, excludeCode = '') => {
  const base = normalizeCouponCode(baseCode);
  if (!base) return '';

  const exclude = normalizeCouponCode(excludeCode);
  const taken = new Set(
    (Array.isArray(takenCodes) ? takenCodes : [...takenCodes])
      .map(normalizeCouponCode)
      .filter((c) => c && c !== exclude),
  );

  if (!taken.has(base)) return base;

  for (let n = 2; n <= 99; n += 1) {
    const candidate = `${base}${n}`.slice(0, MAX_CODE_LENGTH);
    if (!taken.has(candidate)) return candidate;
  }

  const suffix = String(Date.now()).slice(-4);
  return `${base.slice(0, Math.max(1, MAX_CODE_LENGTH - 4))}${suffix}`;
};

export const generateCouponCode = (params, takenCodes, excludeCode = '') => {
  const base = buildCouponCodeBase(params);
  if (!base) return '';
  return ensureUniqueCouponCode(base, takenCodes, excludeCode);
};

/** Fallback when name/price not ready yet — Mitra can edit. */
export const generateDefaultCouponCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let suffix = '';
  for (let i = 0; i < 5; i += 1) {
    suffix += chars[Math.floor(Math.random() * chars.length)];
  }
  return `LOC${suffix}`;
};

export const collectCouponCodesFromList = (coupons = [], excludeId = null) => {
  const exclude = excludeId != null ? String(excludeId) : null;
  return (coupons || [])
    .filter((c) => {
      if (!c) return false;
      const id = String(c.id || c.coupon_id || '');
      return !exclude || id !== exclude;
    })
    .map((c) => normalizeCouponCode(c.coupon_code || c.code))
    .filter(Boolean);
};
